import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase, selectData } from "../lib/supabase";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalInvoices: 0,
    paidInvoices: 0,
    totalRevenue: 0,
    draftInvoices: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    try {
      setLoading(true);

      const result = await selectData("invoices", {
        order: { column: "created_at", ascending: false },
      });

      if (result.error) throw result.error;

      const invoices = result.data || [];

      const totalInvoices = invoices.length;
      const paidInvoices = invoices.filter((inv) => inv.status === "paid").length;
      const draftInvoices = invoices.filter((inv) => inv.status === "draft").length;
      const totalRevenue = invoices
        .filter((inv) => inv.status === "paid")
        .reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);

      setStats({
        totalInvoices,
        paidInvoices,
        totalRevenue,
        draftInvoices,
      });

      setRecentInvoices(invoices.slice(0, 5));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await supabase?.auth?.signOut();
      window.location.href = "/admin-login";
    } catch (error) {
      console.error(error);
    }
  }

  function formatMoney(value, currency = "EUR") {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  }

  function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("fr-FR");
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topbar}>
          <div>
            <p style={styles.eyebrow}>Administration Weblax</p>
            <h1 style={styles.title}>Dashboard admin</h1>
            <p style={styles.subtitle}>
              Accède rapidement à tes outils de gestion.
            </p>
          </div>

          <button style={styles.logoutButton} onClick={handleLogout}>
            Déconnexion
          </button>
        </div>

        <div style={styles.quickGrid}>
          <Link to="/admin/factures" style={styles.cardLink}>
            <div style={styles.card}>
              <div style={styles.icon}>📄</div>
              <h2 style={styles.cardTitle}>Voir les factures</h2>
              <p style={styles.cardText}>
                Consulte la liste complète des factures enregistrées.
              </p>
            </div>
          </Link>

          <Link to="/admin/factures/nouvelle" style={styles.cardLink}>
            <div style={styles.card}>
              <div style={styles.icon}>＋</div>
              <h2 style={styles.cardTitle}>Créer une facture</h2>
              <p style={styles.cardText}>
                Génère une nouvelle facture avec client, produits et taxes.
              </p>
            </div>
          </Link>

          <Link to="/admin/parametres" style={styles.cardLink}>
            <div style={styles.card}>
              <div style={styles.icon}>⚙️</div>
              <h2 style={styles.cardTitle}>Gérer les paramètres d’entreprise</h2>
              <p style={styles.cardText}>
                Modifie les informations société, banque et notes de paiement.
              </p>
            </div>
          </Link>

          <Link to="/admin/produits" style={styles.cardLink}>
            <div style={styles.card}>
              <div style={styles.icon}>📦</div>
              <h2 style={styles.cardTitle}>Gérer les produits</h2>
              <p style={styles.cardText}>
                Ajoute, modifie ou désactive les produits et services.
              </p>
            </div>
          </Link>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Vue d’ensemble</h3>
            <p style={styles.sectionSubtitle}>
              Un aperçu rapide de l’activité récente.
            </p>
          </div>

          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>Factures totales</span>
              <strong style={styles.statValue}>{stats.totalInvoices}</strong>
            </div>

            <div style={styles.statCard}>
              <span style={styles.statLabel}>Factures payées</span>
              <strong style={styles.statValue}>{stats.paidInvoices}</strong>
            </div>

            <div style={styles.statCard}>
              <span style={styles.statLabel}>Brouillons</span>
              <strong style={styles.statValue}>{stats.draftInvoices}</strong>
            </div>

            <div style={styles.statCard}>
              <span style={styles.statLabel}>Montant encaissé</span>
              <strong style={styles.statValue}>
                {formatMoney(stats.totalRevenue)}
              </strong>
            </div>
          </div>

          <div style={styles.recentCard}>
            <div style={styles.recentHeader}>
              <h4 style={styles.recentTitle}>Dernières factures</h4>
              <Link to="/admin/factures" style={styles.seeAllLink}>
                Tout voir
              </Link>
            </div>

            {loading ? (
              <p style={styles.loadingText}>Chargement...</p>
            ) : recentInvoices.length === 0 ? (
              <p style={styles.loadingText}>Aucune facture pour le moment.</p>
            ) : (
              <div style={styles.recentList}>
                {recentInvoices.map((invoice) => (
                  <div key={invoice.id} style={styles.recentItem}>
                    <div>
                      <div style={styles.recentMain}>
                        {invoice.invoice_number || "Facture"}
                      </div>
                      <div style={styles.recentSub}>
                        {invoice.recipient_name || "Client inconnu"} •{" "}
                        {formatDate(invoice.issue_date)}
                      </div>
                    </div>

                    <div style={styles.recentRight}>
                      <div style={styles.recentAmount}>
                        {formatMoney(invoice.total_amount, invoice.currency)}
                      </div>
                      <div style={styles.recentStatus}>{invoice.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0b1020",
    padding: "40px 20px",
    color: "#fff",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    marginBottom: "30px",
    flexWrap: "wrap",
  },
  eyebrow: {
    color: "#8fa4ff",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontSize: "0.8rem",
    marginBottom: "8px",
  },
  title: {
    fontSize: "2.2rem",
    fontWeight: 800,
    marginBottom: "10px",
  },
  subtitle: {
    color: "rgba(255,255,255,0.72)",
    maxWidth: "760px",
    lineHeight: 1.6,
  },
  logoutButton: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "#151c34",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "20px",
    marginBottom: "32px",
  },
  cardLink: {
    textDecoration: "none",
    color: "inherit",
  },
  card: {
    background: "linear-gradient(180deg, #121933 0%, #0f1730 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    padding: "22px",
    minHeight: "210px",
    boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
  },
  icon: {
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #4454ff 0%, #7a49ff 60%, #f0c15b 100%)",
    fontSize: "1.35rem",
    marginBottom: "18px",
  },
  cardTitle: {
    fontSize: "1.08rem",
    fontWeight: 800,
    marginBottom: "10px",
    lineHeight: 1.4,
  },
  cardText: {
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.6,
    fontSize: "0.95rem",
  },
  section: {
    marginTop: "8px",
  },
  sectionHeader: {
    marginBottom: "18px",
  },
  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 800,
    marginBottom: "6px",
  },
  sectionSubtitle: {
    color: "rgba(255,255,255,0.68)",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "16px",
    marginBottom: "20px",
  },
  statCard: {
    background: "#121933",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "18px",
  },
  statLabel: {
    display: "block",
    color: "rgba(255,255,255,0.65)",
    marginBottom: "10px",
    fontSize: "0.92rem",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 800,
  },
  recentCard: {
    background: "#121933",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    padding: "20px",
  },
  recentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  recentTitle: {
    fontSize: "1.08rem",
    fontWeight: 800,
  },
  seeAllLink: {
    color: "#9fb1ff",
    textDecoration: "none",
    fontWeight: 700,
  },
  recentList: {
    display: "grid",
    gap: "12px",
  },
  recentItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    padding: "14px 0",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  recentMain: {
    fontWeight: 700,
    marginBottom: "4px",
  },
  recentSub: {
    color: "rgba(255,255,255,0.65)",
    fontSize: "0.92rem",
  },
  recentRight: {
    textAlign: "right",
  },
  recentAmount: {
    fontWeight: 700,
    marginBottom: "4px",
  },
  recentStatus: {
    color: "#9fb1ff",
    fontSize: "0.9rem",
    textTransform: "capitalize",
  },
  loadingText: {
    color: "rgba(255,255,255,0.68)",
  },
};